import dayjs from "dayjs";
import { TextField } from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const MuiDatePicker = ({
  label = "Select Date",
  value = null,
  onChange,
  minDate = null,
  maxDate = null,
  disabled = false,
  readOnly = false,
  error = false,
  helperText = "",
  fullWidth = true,
  variant = "outlined",
  size = "medium",
  margin = "normal",
  required = false,
  disablePast = false,
  disableFuture = false,
  format = "MM/dd/yyyy",
  views = null,
  openTo = "day",
  orientation = "portrait",
  isLocalAuction = false, // New prop to identify local auctions
}) => {
  // Function to disable dates that aren't Monday, Wednesday, or Friday for local auctions
  const shouldDisableDate = (date) => {
    if (!isLocalAuction) return false;

    // Safely get the day of the week (0 = Sunday, 6 = Saturday)
    const day = date.day();  

    // Allow only Mon (1), Wed (3), Fri (5)
    return day !== 1 && day !== 3 && day !== 5;
  };

  return (
    <DatePicker
      // label={label}
      value={dayjs(value)}
      // onChange={onChange}
      onChange={(newValue) => {
        onChange?.(newValue?.toDate?.() ?? null);
      }}
      minDate={dayjs(minDate)}
      maxDate={dayjs(maxDate)}
      disabled={disabled}
      readOnly={readOnly}
      shouldDisableDate={shouldDisableDate}
      disablePast={disablePast}
      disableFuture={disableFuture}
      openTo={openTo}
      views={views}
      orientation={orientation}
      inputFormat={format}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: 40,
        },
        "& .MuiInputBase-input": {
          height: "100%",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          sx={{
            "& .MuiInputBase-root": {
              height: 40,
            },
            "& .MuiInputLabel-root": {
              paddingBottom: "8px", // Add padding to the bottom of the label
              marginTop: "2px", // Adjust top margin to better position the label
            },
            "& .MuiFormLabel-root": {
              transform: "translate(14px, -12px) scale(0.75)", // Adjust the vertical position of the label
            },
          }}
          variant={variant}
          size={size}
          margin={margin}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

export default MuiDatePicker;

export const MuiTimePicker = ({
  label = "Select Time",
  value = null,
  onChange,
  minTime = null,
  maxTime = null,
  disabled = false,
  readOnly = false,
  error = false,
  helperText = "",
  fullWidth = true,
  variant = "outlined",
  size = "medium",
  margin = "normal",
  required = false,
  disablePast = false,
  disableFuture = false,
  format = "hh:mm a",
  views = ["hours", "minutes"],
  openTo = "hours",
  orientation = "portrait",
  ampm = true,
  minutesStep = 30,
}) => {
  return (
    <MobileTimePicker
      label={label}
      value={dayjs(value)}
      // onChange={onChange}
      onChange={(newValue) => {
        onChange?.(newValue?.toDate?.() ?? null);
      }}
      minTime={dayjs(minTime)}
      maxTime={dayjs(maxTime)}
      disabled={disabled}
      readOnly={readOnly}
      disablePast={disablePast}
      disableFuture={disableFuture}
      openTo={openTo}
      views={views}
      orientation={orientation}
      inputFormat={format}
      ampm={ampm}
      minutesStep={minutesStep}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: 40,
          color: "primary.main",
        },
        "& .MuiInputBase-input": {
          height: "100%",
          color: "primary.main",
        },
      }}
      slotProps={{
        toolbar: {
          sx: {
            // Style for selected text
            "& .MuiTypography-root.Mui-selected": {
              fontSize: "1.2rem",
              fontWeight: "bold",
            },
            // Style for non-selected text
            "& .MuiTypography-root:not(.Mui-selected)": {
              color: "text.disabled",
              fontSize: "1.2rem",
            },
            // Ensuring AM/PM selection has proper styling
            "& .MuiTimePickerToolbar-ampmSelection": {
              "& .MuiTypography-root.Mui-selected": {
                fontWeight: "bold",
              },
              "& .MuiTypography-root:not(.Mui-selected)": {
                color: "text.disabled",
              },
            },
            // Hour and minute selection container
            "& .MuiTimePickerToolbar-hourMinuteLabel": {
              alignItems: "center",
            },
          },
        },
        // Style for the textfield
        textField: {
          fullWidth: fullWidth,
          variant: variant,
          size: size,
          margin: margin,
          required: required,
          error: error,
          helperText: helperText,
          sx: {
            "& .MuiInputBase-root": {
              color: "primary.main",
              height: 40,
            },
          },
        },
      }}
    />
  );
};
